/**
 * Utility functions for the OrderBook component
 */

import type { Orders } from '@/types/api/order-book'

import { isNil } from 'lodash'

import { OrderSide } from '@/types/enums/order'
import { adjustPriceBySideAndPrecision } from '@/utils/order-calculations'
import { convertParValueToNumBonds } from '@/utils/presentation/bonds'
import { formatMoney, PLACEHOLDER } from '@/utils/presentation/formatting'

import {
  getDisplayPriceValue,
  getDisplaySpreadValue,
  getDisplayYieldValue,
} from './display-value-helpers'
import {
  ActivePanelType,
  type EnrichedOrderBookResponse,
  type EnrichedOrderBookRowItem,
  type EnrichedOrders,
  type OrderBookRowItem,
  type OrderSummaryData,
  QuoteSide,
} from './types'

function isEnrichedOrder(order: unknown): order is EnrichedOrderBookResponse {
  return !!order && typeof order === 'object' && 'source' in order
}

// =========================================
// OrderBook Height Configuration
// =========================================

/**
 * Configuration constants for OrderBook height calculations
 */
export const ORDER_BOOK_HEIGHT_CONFIG = {
  ROW_HEIGHT_STANDARD: 38,
  ROW_HEIGHT_WITH_FEES: 48,
  HEADER_ROWS: 1, // Number of header rows in the container
} as const

/**
 * Gets the row height based on whether pre/post fee values are shown
 * @param showPreAndPostFeeValues - Whether to show pre and post fee values
 * @returns The height in pixels for each row
 */
export function getRowHeight(showPreAndPostFeeValues: boolean): number {
  return showPreAndPostFeeValues
    ? ORDER_BOOK_HEIGHT_CONFIG.ROW_HEIGHT_WITH_FEES
    : ORDER_BOOK_HEIGHT_CONFIG.ROW_HEIGHT_STANDARD
}

/**
 * Calculates the maximum height for the OrderBook container
 * @param rows - Number of data rows to display
 * @param showPreAndPostFeeValues - Whether to show pre and post fee values
 * @returns The maximum height in pixels for the container
 */
export function calculateMaxHeight(
  rows: number,
  showPreAndPostFeeValues: boolean,
): number {
  const rowHeight = getRowHeight(showPreAndPostFeeValues)
  return rowHeight * (rows + ORDER_BOOK_HEIGHT_CONFIG.HEADER_ROWS)
}

/**
 * Builds an array of order book row objects from a collection of orders.
 *
 * @param {Orders | EnrichedOrders} orders - The collection of orders to use to build the order book.
 * @returns {OrderBookRowItem[]} An array of unified order book data.
 */
export function buildOrderBook(
  orders: Orders | EnrichedOrders = { bids: [], asks: [] },
  showPreAndPostFeeValues = false,
): OrderBookRowItem[] {
  const { bids, asks } = orders

  const orderRange = buildRange(Math.max(bids?.length, asks?.length))

  // In the order book, all values are in number of bonds
  // We convert back out to par value only in API calls and callbacks
  const formattedOrders = orderRange.map((__, idx): OrderBookRowItem => {
    const bid = bids[idx]
    const ask = asks[idx]

    const bidPrice = bid
      ? getDisplayPriceValue(bid, showPreAndPostFeeValues)
      : null
    const askPrice = ask
      ? getDisplayPriceValue(ask, showPreAndPostFeeValues)
      : null
    const bidYieldToWorst = bid
      ? getDisplayYieldValue(bid, showPreAndPostFeeValues)
      : null
    const askYieldToWorst = ask
      ? getDisplayYieldValue(ask, showPreAndPostFeeValues)
      : null
    const bidSpreadToBenchmarkBps = bid
      ? getDisplaySpreadValue(bid, showPreAndPostFeeValues)
      : null
    const askSpreadToBenchmarkBps = ask
      ? getDisplaySpreadValue(ask, showPreAndPostFeeValues)
      : null

    return {
      bidVenue: bid?.venue_mpid ?? null,
      bidMinNumBonds: bid ? convertParValueToNumBonds(bid.min_size) : null,
      bidNumBonds: bid ? convertParValueToNumBonds(bid.size) : null,
      bidMinBalanceRemaining: bid?.min_balance_remaining
        ? convertParValueToNumBonds(bid.min_balance_remaining)
        : null,
      bidMinIncrement: bid?.min_increment
        ? convertParValueToNumBonds(bid.min_increment)
        : null,
      bidPrice,
      bidYieldToWorst,
      bidSpreadToBenchmarkBps,
      bidSource: isEnrichedOrder(bid) ? bid.source : null,
      bidDisabled: isEnrichedOrder(bid) ? bid.disabled : undefined,
      bidHighlighted: isEnrichedOrder(bid) ? bid.highlighted : undefined,
      bidRfqQuoteStatus: isEnrichedOrder(bid) ? bid.rfqQuoteStatus : undefined,
      bidRfqId: isEnrichedOrder(bid) ? bid.rfqId : null,
      bidVenueQuoteId: isEnrichedOrder(bid) ? bid.venueQuoteId : null,
      askVenue: ask?.venue_mpid ?? null,
      askMinNumBonds: ask ? convertParValueToNumBonds(ask.min_size) : null,
      askNumBonds: ask ? convertParValueToNumBonds(ask.size) : null,
      askMinBalanceRemaining: ask?.min_balance_remaining
        ? convertParValueToNumBonds(ask.min_balance_remaining)
        : null,
      askMinIncrement: ask?.min_increment
        ? convertParValueToNumBonds(ask.min_increment)
        : null,
      askPrice,
      askYieldToWorst,
      askSpreadToBenchmarkBps,
      askSource: isEnrichedOrder(ask) ? ask.source : null,
      askDisabled: isEnrichedOrder(ask) ? ask.disabled : undefined,
      askHighlighted: isEnrichedOrder(ask) ? ask.highlighted : undefined,
      askRfqQuoteStatus: isEnrichedOrder(ask) ? ask.rfqQuoteStatus : undefined,
      askRfqId: isEnrichedOrder(ask) ? ask.rfqId : null,
      askVenueQuoteId: isEnrichedOrder(ask) ? ask.venueQuoteId : null,
      askAdvisorConcession: ask?.advisor_concession ?? null,
    }
  })
  return formattedOrders as OrderBookRowItem[]
}

/**
 * Builds a summary data object from a collection of orders.
 *
 * @param {EnrichedOrderBookRowItem[]} bids - The collection of bids to summarize.
 * @param {EnrichedOrderBookRowItem[]} asks - The collection of asks to summarize.
 * @param {number} bidCount - The number of bids in the order collection.
 * @param {number} askCount - The number of asks in the order collection.
 * @returns {OrderSummaryData} A data object containing summary information about the bids and asks in the order collection.
 */
export function buildSummaryData(
  bids: EnrichedOrderBookRowItem[],
  asks: EnrichedOrderBookRowItem[],
  bidCount: number,
  askCount: number,
): OrderSummaryData {
  const firstValidBid = bids.find((bid) => !bid.disabled)
  const lastValidBid = bids.findLast((bid) => !bid.disabled)
  const firstValidAsk = asks.find((ask) => !ask.disabled)
  const lastValidAsk = asks.findLast((ask) => !ask.disabled)

  const topBidPrice =
    typeof firstValidBid?.bidPrice === 'object'
      ? firstValidBid?.bidPrice?.preFeePrice
      : firstValidBid?.bidPrice
  const bottomBidPrice =
    typeof lastValidBid?.bidPrice === 'object'
      ? lastValidBid?.bidPrice?.preFeePrice
      : lastValidBid?.bidPrice
  const topAskPrice =
    typeof firstValidAsk?.askPrice === 'object'
      ? firstValidAsk?.askPrice?.preFeePrice
      : firstValidAsk?.askPrice
  const bottomAskPrice =
    typeof lastValidAsk?.askPrice === 'object'
      ? lastValidAsk?.askPrice?.preFeePrice
      : lastValidAsk?.askPrice

  // Current logic is to round Order Book prices to 4 decimal places
  // We use adjustPriceBySideAndPrecision, which rounds the price based
  // on the side of the book (e.g. buys round up, sells round down)
  // This matches the logic we use to prefill limit prices in the order entry form
  // and LOB execution orders, with the cushion increasing the chances of execution
  const formattedTopBid = formatOrderBookQuotePrice(
    topBidPrice,
    OrderSide.Sell, // Market bids on what you are selling
  )
  const formattedBottomBid = formatOrderBookQuotePrice(
    bottomBidPrice,
    OrderSide.Sell,
  )
  const formattedTopAsk = formatOrderBookQuotePrice(
    topAskPrice,
    OrderSide.Buy, // Market asks on what you are buying
  )
  const formattedBottomAsk = formatOrderBookQuotePrice(
    bottomAskPrice,
    OrderSide.Buy,
  )

  const bidEnabledCount = bids.filter((bid) => !bid.disabled).length
  const askEnabledCount = asks.filter((ask) => !ask.disabled).length

  return {
    bidCount,
    bidRange: createSpreadLabel(formattedBottomBid, formattedTopBid),
    marketMid: `${calculateBidAskSpread(topBidPrice, topAskPrice)}`,
    askRange: createSpreadLabel(formattedTopAsk, formattedBottomAsk),
    askCount,
    bidEnabledCount,
    askEnabledCount,
  }
}

/**
 * Creates an array of consecutive integers representing a numerical range.
 *
 * @param {number} rangeSize - The number of elements to include in the range (e.g., 10 will generate numbers 0 through 9).
 * @returns {number[]} An array containing the integers from 0 to `rangeSize - 1`.
 */
export function buildRange(rangeSize: number): number[] {
  return [...Array(rangeSize).keys()]
}

/**
 * Calculates and returns the bid-ask spread as a percentage of the ask price.
 *
 * @param {number} bid - The current best bid price (the highest price that buyers are willing to pay).
 * @param {number} ask - The current best ask price (the lowest price that sellers are willing to sell for).
 * @returns {string} A string representing the spread as a percentage, rounded to 3 decimal places.
 */
export function calculateBidAskSpread(
  bid: number | null | undefined,
  ask: number | null | undefined,
): string {
  const adjustedBid = adjustPriceBySideAndPrecision(bid, OrderSide.Sell)
  const adjustedAsk = adjustPriceBySideAndPrecision(ask, OrderSide.Buy)
  if (isNil(adjustedBid) || isNil(adjustedAsk)) {
    return PLACEHOLDER
  }
  return formatMoney(adjustedAsk - adjustedBid, {
    numDigits: 4,
    addTrailingZeros: true,
  })
}

/**
 * Creates a string label representing a numerical range.
 *
 * @param {string} start - The starting value of the range (inclusive).
 * @param {string} end - The ending value of the range (inclusive).
 * @returns {string} A string in the format "start-end" with 3 decimal places for each value.
 */
export function createSpreadLabel(start: string, end: string): string {
  if (start === PLACEHOLDER || end === PLACEHOLDER) {
    return PLACEHOLDER
  }
  return `${start} - ${end}`
}

/**
 * Formats an order book quote price.
 * It adjusts the price by side and precision, then formats it as money.
 * Main principles that this incorporates:
 *  - all order book prices are rounded to 4 decimal places
 *  - we round up for buys, round down for sells, to increase the likelihood of execution
 *
 * @param {number | null | undefined} price - The price to format.
 * @param {OrderSide} side - The side of the order (Buy or Sell).
 * @returns {string} The formatted price string.
 */
export function formatOrderBookQuotePrice(
  price: number | null | undefined,
  side: OrderSide,
): string {
  if (isNil(price)) {
    return PLACEHOLDER
  }
  const adjustedPrice = adjustPriceBySideAndPrecision(price as number, side)
  return formatMoney(adjustedPrice ?? 0, {
    numDigits: 4,
    addTrailingZeros: true,
  })
}

/**
 * Generates summary count text for bids or asks in the order book summary banner.
 * Shows enabled count primarily, with disabled count and N/A in parentheses if applicable.
 *
 * @param enabledCount - Number of actionable quotes
 * @param totalCount - Total number of quotes
 * @param label - Label to display (e.g., "Bids" or "Asks")
 * @returns Formatted string like "Bids 4" or "Bids 4 (6 N/A)"
 *
 * @example
 * getSummaryCountText(10, 10, 'Bids') // "Bids 10"
 * getSummaryCountText(4, 10, 'Bids')  // "Bids 4 (6 N/A)"
 * getSummaryCountText(0, 10, 'Asks')  // "Asks 0 (10 N/A)"
 */
export function getSummaryCountText(
  enabledCount: number,
  totalCount: number,
  label: string,
): string {
  const nonActionableCount = totalCount - enabledCount
  const baseText = `${label} ${enabledCount}`

  if (nonActionableCount > 0) {
    return `${baseText} (${nonActionableCount} N/A)`
  }

  return baseText
}

export function isPanelActive(
  panel: ActivePanelType,
  activePanel: ActivePanelType,
): boolean {
  return activePanel === panel || activePanel === ActivePanelType.Both
}

/**
 * Checks if a provided number of bonds is below the minimum threshold.
 * Returns false if either the minimum threshold or provided bonds is not available.
 *
 * @param {number | null} providedBonds - The number of bonds being checked
 * @param {number | undefined} minThreshold - The minimum number of bonds required
 * @returns {boolean} True if provided bonds is below the minimum threshold, false otherwise
 */
export function isProvidedNumBondsBelowMin(
  providedBonds: number | null | undefined,
  minThreshold: number | null | undefined,
): boolean {
  if (isNil(minThreshold) || isNil(providedBonds)) {
    return false
  }
  return minThreshold > providedBonds
}

/**
 * Checks if a provided number of bonds is above the maximum threshold.
 * Returns false if either the maximum threshold or provided bonds is not available.
 *
 * @param {number | null} providedBonds - The number of bonds being checked
 * @param {number | undefined} maxThreshold - The maximum number of bonds required
 * @returns {boolean} True if provided bonds is above the maximum threshold, false otherwise
 */
export function isProvidedNumBondsAboveMax(
  providedBonds: number | null | undefined,
  maxThreshold: number | null | undefined,
): boolean {
  if (isNil(maxThreshold) || isNil(providedBonds)) {
    return false
  }
  return providedBonds > maxThreshold
}

/**
 * Enriches order book row items with a disabled boolean property based on the min and max number of bonds.
 * We need to pass the onRowAction function to check if the quote is above/below the min/max number of bonds.
 *
 * @param {OrderBookRowItem[]} quotes - The quotes to enrich.
 * @param {number | null | undefined} minNumBonds - The minimum number of bonds.
 * @param {number | null | undefined} maxNumBonds - The maximum number of bonds.
 * @param {function} onRowAction - The function to call when the row is clicked.
 * @returns {EnrichedOrderBookRowItem[]} The enriched quotes.
 */
export function enrichOrderBookRowItems(
  quotes: OrderBookRowItem[],
  minNumBonds: number | null | undefined,
  maxNumBonds: number | null | undefined,
  quoteType: QuoteSide,
): EnrichedOrderBookRowItem[] {
  const isBid = quoteType === QuoteSide.Bid
  return quotes.map((quote) => ({
    ...quote,
    disabled:
      !quote ||
      isNil(isBid ? quote.bidPrice : quote.askPrice) ||
      (isBid ? quote.bidDisabled : quote.askDisabled) ||
      isProvidedNumBondsBelowMin(
        minNumBonds,
        isBid ? quote.bidMinNumBonds : quote.askMinNumBonds,
      ) ||
      isProvidedNumBondsAboveMax(
        maxNumBonds,
        isBid ? quote.bidNumBonds : quote.askNumBonds,
      ),
    highlighted:
      (isBid ? quote?.bidHighlighted : quote?.askHighlighted) ?? false,
  }))
}

/**
 * Splits bids and asks (based on empty bidPrice/askPrice).
 * Enriches bids and asks.
 * Optionally filters.
 * @param orders Quotes to enrich and filter
 * @param shouldFilter Whether to filter out disabled quotes
 * @param minNumBonds Minimum number of bonds (used in disabled determination)
 * @param maxNumBonds Maximum number of bonds (used in disabled determination)
 * @returns Enriched, filtered bids and asks + whether bids/asks can be filtered.
 */
export const enrichAndFilterOrders = (
  orders: OrderBookRowItem[],
  shouldFilter: boolean,
  minNumBonds?: number,
  maxNumBonds?: number,
) => {
  const enrichedBids = enrichOrderBookRowItems(
    orders.filter((order) => order.bidPrice !== null),
    minNumBonds,
    maxNumBonds,
    QuoteSide.Bid,
  )

  const enrichedAsks = enrichOrderBookRowItems(
    orders.filter((order) => order.askPrice !== null),
    minNumBonds,
    maxNumBonds,
    QuoteSide.Ask,
  )

  const canFilter = [...enrichedBids, ...enrichedAsks].some((e) => e.disabled)

  if (shouldFilter) {
    return {
      enrichedFilteredAsks: enrichedAsks.filter((b) => !b.disabled),
      enrichedFilteredBids: enrichedBids.filter((b) => !b.disabled),
      canFilter,
    }
  }

  return {
    enrichedFilteredAsks: enrichedAsks,
    enrichedFilteredBids: enrichedBids,
    canFilter,
  }
}
